import React from 'react';
import {
  Card,
  Typography,
  Space,
  Button,
  Alert,
  Statistic,
  Row,
  Col,
  Spin,
  Modal,
  message,
  Input,
  Form,
} from 'antd';
import {
  DatabaseOutlined,
  PlayCircleOutlined,
  DeleteOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined
} from '@ant-design/icons';
import {
  useGetMockDataStatusQuery,
  useGenerateTestMockDataMutation,
  useGenerateFullMockDataMutation,
  useCleanMockDataMutation
} from '../../../services/nestjs-base-api';
import { useAuth } from '../../../hooks/useAuth';

const { Title, Paragraph, Text } = Typography;
const { confirm } = Modal;

export const MockDataTab: React.FC = () => {
  const [form] = Form.useForm();
  const { user } = useAuth();
  const jwtToken = user?.jwtToken || '';

  // RTK Query hooks
  const { data: stats, error: statsError, isLoading: loading, refetch } = useGetMockDataStatusQuery({ jwtToken });
  const [generateTestData, { isLoading: testLoading }] = useGenerateTestMockDataMutation();
  const [generateFullData, { isLoading: fullLoading }] = useGenerateFullMockDataMutation();
  const [cleanData, { isLoading: cleanLoading }] = useCleanMockDataMutation();

  const handleTestDataGeneration = async () => {
    try {
      const result = await generateTestData({ jwtToken }).unwrap();
      if (result.success) {
        message.success(result.message);
        refetch();
      } else {
        message.error(result.message);
      }
    } catch (error: any) {
      message.error(error?.data?.message || 'Failed to generate test data');
    }
  };

  const handleFullDataGeneration = async (options: { months: number; responsesPerMonth: number }) => {
    try {
      const result = await generateFullData({
        jwtToken,
        payload: options
      }).unwrap();
      if (result.success) {
        message.success(result.message);
        refetch();
      } else {
        message.error(result.message);
      }
    } catch (error: any) {
      message.error(error?.data?.message || 'Failed to generate full data');
    }
  };

  const handleCleanData = async () => {
    try {
      const result = await cleanData({ jwtToken }).unwrap();
      if (result.success) {
        message.success(result.message);
        refetch();
      } else {
        message.error(result.message);
      }
    } catch (error: any) {
      message.error(error?.data?.message || 'Failed to clean mock data');
    }
  };

  const confirmTestData = () => {
    confirm({
      title: 'Generate Test Mock Data?',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>This will create:</p>
          <ul>
            <li>1 mock organization</li>
            <li>1 mock brand</li>
            <li>1 mock survey</li>
            <li>~1800 survey responses (12 months × 150 responses)</li>
          </ul>
          <p>Are you sure you want to proceed?</p>
        </div>
      ),
      onOk: handleTestDataGeneration,
    });
  };

  const confirmFullGeneration = () => {
    const values = form.getFieldsValue();

    confirm({
      title: 'Generate Full Mock Data?',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>This will create:</p>
          <ul>
            <li>~10 mock organizations</li>
            <li>~10 mock brands</li>
            <li>~10 mock surveys</li>
            <li>~{values.months * values.responsesPerMonth * 10} survey responses</li>
          </ul>
          <p><strong>This operation may take several minutes to complete.</strong></p>
          <p>Are you sure you want to proceed?</p>
        </div>
      ),
      onOk: () => handleFullDataGeneration(values),
    });
  };

  const confirmCleanData = () => {
    confirm({
      title: 'Delete All Mock Data?',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <Alert
            message="This action is IRREVERSIBLE"
            description="All mock organizations, brands, surveys, and responses will be permanently deleted."
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <p>Are you absolutely sure you want to proceed?</p>
        </div>
      ),
      okText: 'Yes, Delete All',
      okType: 'danger',
      onOk: handleCleanData,
    });
  };

  return (
    <div>
      <Card
        style={{
          background: 'linear-gradient(135deg, #ff4d4f 0%, #d32f2f 100%)',
          border: 'none',
          marginBottom: 24,
        }}
      >
        <div>
          <Title level={3} style={{ margin: 0, color: 'white', display: 'flex', alignItems: 'center' }}>
            <WarningOutlined style={{ marginRight: 8 }} />
            Mock Data Management - Danger Zone
          </Title>
          <Text style={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 500, lineHeight: 1.5 }}>
            <strong>WARNING:</strong> Mock data operations are IRREVERSIBLE and can permanently delete data.
            These tools should only be used in development/staging environments by users who understand the implications.
          </Text>
        </div>
      </Card>

      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Text type="secondary">
            Create test data or clean existing mock data from the system
          </Text>
        </div>
        <Button
          icon={<ReloadOutlined style={{ marginRight: 4 }} />}
          onClick={() => refetch()}
          loading={loading}
        >
          Refresh Status
        </Button>
      </div>

      {/* Status Card */}
      <Card title="Current Mock Data Status" style={{ marginBottom: '24px' }}>
        {loading ? (
          <Spin />
        ) : statsError ? (
          <Alert message="Failed to load status" type="error" />
        ) : stats ? (
          <div>
            <Alert
              message={stats.hasData ? "Mock Data Present" : "No Mock Data"}
              description={stats.hasData ?
                "Your system currently contains mock data." :
                "Your system is clean - no mock data detected."
              }
              type={stats.hasData ? "warning" : "success"}
              showIcon
              style={{ marginBottom: '16px' }}
            />

            <Row gutter={16}>
              <Col span={4}>
                <Statistic
                  title="Organizations"
                  value={stats.stats.organisations}
                  prefix={<DatabaseOutlined />}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="Brands"
                  value={stats.stats.brands}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="Surveys"
                  value={stats.stats.surveys}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Survey Responses"
                  value={stats.stats.surveyResponses}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Question Responses"
                  value={stats.stats.questionResponses}
                />
              </Col>
            </Row>
          </div>
        ) : null}
      </Card>

      {/* Operations */}
      <Row gutter={[24, 24]}>
        {/* Test Data Generation */}
        <Col xs={24} lg={12}>
          <Card title="Test Data Generation" style={{ height: '100%' }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Paragraph>
                Generate a small dataset for testing purposes.
              </Paragraph>
              <ul>
                <li>1 organization</li>
                <li>1 brand</li>
                <li>12 months of data</li>
                <li>~150 responses per month</li>
              </ul>
              <Button
                type="primary"
                icon={<PlayCircleOutlined style={{ marginRight: 4 }} />}
                onClick={confirmTestData}
                disabled={stats?.hasData && !loading}
                loading={testLoading}
                block
              >
                Generate Test Data
              </Button>
              {stats?.hasData && (
                <Text type="secondary">
                  <WarningOutlined style={{ marginRight: 4 }} /> Mock data already exists. Clean first to regenerate.
                </Text>
              )}
            </Space>
          </Card>
        </Col>

        {/* Full Data Generation */}
        <Col xs={24} lg={12}>
          <Card title="Full Data Generation" style={{ height: '100%' }}>
            <Form form={form} layout="vertical" initialValues={{ months: 12, responsesPerMonth: 150 }}>
              <Paragraph>
                Generate complete mock dataset with multiple brands.
              </Paragraph>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Months" name="months">
                    <Input type="number" min={1} max={24} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Responses/Month" name="responsesPerMonth">
                    <Input type="number" min={50} max={500} />
                  </Form.Item>
                </Col>
              </Row>

              <Button
                type="primary"
                icon={<DatabaseOutlined style={{ marginRight: 4 }} />}
                onClick={confirmFullGeneration}
                disabled={stats?.hasData && !loading}
                loading={fullLoading}
                block
              >
                Generate Full Dataset
              </Button>
              {stats?.hasData && (
                <Text type="secondary">
                  <WarningOutlined style={{ marginRight: 4 }} /> Mock data already exists. Clean first to regenerate.
                </Text>
              )}
            </Form>
          </Card>
        </Col>

        {/* Clean Data */}
        <Col xs={24}>
          <Card title="Data Cleanup" style={{ borderColor: '#ff4d4f' }}>
            <Alert
              message="Danger Zone"
              description="Permanently delete all mock data from the system."
              type="error"
              showIcon
              style={{ marginBottom: '16px' }}
            />

            <Button
              type="primary"
              danger
              icon={<DeleteOutlined style={{ marginRight: 4 }} />}
              onClick={confirmCleanData}
              disabled={!stats?.hasData && !loading}
              loading={cleanLoading}
              size="large"
            >
              Delete All Mock Data
            </Button>
            {!stats?.hasData && (
              <div style={{ marginTop: '8px' }}>
                <Text type="secondary">
                  <CheckCircleOutlined style={{ marginRight: 4 }} /> No mock data to clean.
                </Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};